import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import jsPDF from 'jspdf';
import { backendApiGateway } from '~/client/backend';
import { captureException } from '~/hooks/useExceptionLogger';
import { DownloadXMLBody } from '~/types/flow';
import { waitForImagesToLoad } from './images';
import { JsonAPIQueryType } from './jsonapi-query';

export const downloadFileAsBuffer = async (url: string, contentType: string): Promise<ArrayBuffer | undefined> => {
  try {
    const result = await axios.get<ArrayBuffer>(url, {
      responseType: 'arraybuffer',
      headers: {
        Accept: contentType,
        'Content-Type': contentType,
      },
    });
    return result.data;
  } catch (error) {
    captureException(error, {
      extra: {
        url,
        content_type: contentType,
        help_text: 'something when wrong during file download',
      },
    });
    return;
  }
};

export const openUrl = (urlString: string) => {
  const link = document.createElement('a');
  link.href = urlString;
  link.target = '_blank';
  link.click();
};

export const downloadUrl = (urlString: string, filename = '') => {
  const link = document.createElement('a');
  link.href = urlString;
  link.setAttribute('download', filename);
  link.click();
};

export const downloadBlob = (data: Blob, filename: string) => {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.click();
};

export const downloadExcel = async (
  downloadURL: string,
  params: JsonAPIQueryType,
  defaultFilename: string,
): Promise<void> => {
  const { data, headers } = await backendApiGateway.get<Blob>(downloadURL, {
    headers: {
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    responseType: 'blob',
    params,
  });

  const contentDisposition: string | undefined = headers['content-disposition'];
  const filename = contentDisposition?.split('filename=')[1]?.split(';')[0] || defaultFilename;

  downloadBlob(data, filename);
};

export const downloadXML = async (
  downloadURL: string,
  params: JsonAPIQueryType,
  body: DownloadXMLBody,
  defaultFilename: string,
): Promise<void> => {
  const { data, headers } = await backendApiGateway.post<Blob>(downloadURL, body, {
    headers: {
      accept: 'application/xml',
      'Content-Type': 'application/json',
    },
    responseType: 'blob',
    params,
  });

  const contentDisposition: string | undefined = headers['content-disposition'];
  const filename = contentDisposition?.split('filename=')[1]?.split(';')[0] || defaultFilename;

  downloadBlob(data, filename);
};

interface RenderToPDFOptions {
  element: HTMLDivElement;
  filename: string;
}

const renderToPDF = async ({ element, filename }: RenderToPDFOptions): Promise<void> => {
  const doc = new jsPDF('p', 'mm', 'a4');

  element.style.fontFamily = 'Public Sans, sans-serif';
  element.style.letterSpacing = '0px';
  element.style.wordSpacing = '0px';

  await waitForImagesToLoad(element);

  await doc.html(element, {
    callback: (doc: jsPDF) => {
      doc.save(filename);
    },
    fontFaces: [
      {
        family: 'Public Sans',
        weight: 600,
        src: [
          {
            url: '/static/fonts/PublicSans-SemiBold.ttf',
            format: 'truetype',
          },
        ],
      },
      {
        family: 'Public Sans',
        weight: 500,
        src: [
          {
            url: '/static/fonts/PublicSans-Light.ttf',
            format: 'truetype',
          },
        ],
      },
    ],
    autoPaging: 'text',
    filename,
    html2canvas: {
      async: true,
      useCORS: true,
      allowTaint: false,
      imageTimeout: 15000,
    },
    margin: [5, 0, 8, 0],
    width: 211,
    windowWidth: 1000,
  });
};

export const useRenderToPDF = () =>
  useMutation<void, Error, RenderToPDFOptions>({
    mutationFn: renderToPDF,
  });
